import {
  BufferGeometry,
  Group,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  PanelDocument,
  PanelUI,
  Quaternion,
  SphereGeometry,
  Vector3,
  type Entity,
  type UIKitDocument,
  type World,
} from '@iwsdk/core';
import { buildHandMenuTiles, tileColors, withAudioState, type HandMenuActions, type HandMenuTileState } from '../components/handMenu';
import { HandMenuAnchor, type GripPose, type Handedness, type PalmPose } from '../components/handAnchor';
import { offHandOf, readGripPose, readPalmPose } from '../components/xrHandPose';
import { WindowFollower, type ViewerPose } from '../components/windowFollower';
import { v3, type Vec3 } from '../components/math';
import {
  HAND_MENU_IDS,
  HAND_MENU_MARKUP,
  LABEL_PILL_IDS,
  LABEL_PILL_MARKUP,
  STEP_FORM_IDS,
  STEP_FORM_MARKUP,
  markupUrl,
} from '../markup';
import {
  accentA,
  dangerBg,
  dangerBorder,
  dangerText,
  hintBorder,
  overlay,
  panel,
  panelBorder,
  secondaryBg,
  secondaryBorder,
  type ThemePalette,
} from '../theme/palette';
import { hasPresentation, MAX_ASSISTANT_CARDS, MAX_OPTIONS, MAX_TICKS, type AssistantCardView, type TrainingStepView } from './views';

/** The UX tuning knobs - port of the Unity `UxSettings` defaults. */
export interface UxSettings {
  readonly windowPlacement: 'fixed' | 'headLocked';
  readonly windowDistanceMeters: number;
  readonly windowFollowSeconds: number;
  readonly labelClearanceDegrees: number;
  /** World scale (markup px -> metres) for the label pill. */
  readonly labelScale: number;
  readonly labelLineWidthMeters: number;
  readonly labelDotDiameterMeters: number;
}

export const DEFAULT_UX_SETTINGS: UxSettings = {
  windowPlacement: 'fixed',
  windowDistanceMeters: 1.25,
  windowFollowSeconds: 0.3,
  labelClearanceDegrees: 25,
  labelScale: 0.003,
  labelLineWidthMeters: 0.008,
  labelDotDiameterMeters: 0.056,
};

export interface TrainingUxHostOptions {
  readonly world: World;
  readonly palette: ThemePalette;
  /** How far in front of the user the step form anchors, in meters. */
  readonly formDistanceMeters?: number;
  /** Invoked with the option index when an action button is pressed. */
  readonly onOptionPressed: (optionIndex: number) => void;
  readonly menuActions: HandMenuActions;
  /** Eyebrow text on the form's top-right (e.g. the product/course name). */
  readonly brand?: string;
  readonly settings?: Partial<UxSettings>;
  /** The hand that points (the menu lives on the other one). Default right. */
  readonly pointerHand?: Handedness;
}

/** Markup px -> metres, matching the Unity kit's canvas scales. */
const FORM_PIXELS_TO_METRES = 0.0011;
const MENU_PIXELS_TO_METRES = 0.0005;
const FORM_WIDTH_PX = 520;
const MENU_WIDTH_PX = 76;
const LABEL_LIFT_METERS = 0.16;

/** A uikit element, typed to what this host uses of it. */
interface UiElement {
  setProperties(props: Record<string, unknown>): void;
  addEventListener(type: string, listener: (event: unknown) => void): void;
}

/**
 * The training UX, built from the kit - port of the Unity
 * `TrainingUxController` onto IWSDK panels:
 *
 * - Step form: a world-space focus card (eyebrow, progress ticks, title,
 *   description, optional image, action buttons), placed by a
 *   `WindowFollower` per the shared settings. Slots are shown/hidden per step.
 * - Hand menu: the vertical strip with a live current-step readout, lazily
 *   following the off hand (real palm joint when hand tracking is active,
 *   controller grip otherwise) and shown only in the palm-up pose.
 * - Location indicator: a marker at a host-supplied world point with a
 *   leader line up to a billboarded label pill.
 * - Assistant grid: remote-assistance cards above the step form, one per
 *   connected assistant. No assistants, no grid.
 *
 * The host owns three IWSDK panel entities and drives them from `update()`,
 * which the app calls once per frame (from a small ECS system).
 */
export class TrainingUxHost {
  private readonly world: World;
  private readonly palette: ThemePalette;
  private readonly settings: UxSettings;
  private readonly onOptionPressed: (optionIndex: number) => void;
  private readonly brand: string;
  private readonly offHand: Handedness;

  // Step form.
  private readonly formEntity: Entity;
  private readonly formUrl: string;
  private formDoc: UIKitDocument | null = null;
  private formFollower: WindowFollower | null = null;
  private pendingStep: TrainingStepView | null = null;
  private formContentVisible = false;
  private optionsInteractable = true;
  private optionCount = 0;
  private assistantCards: AssistantCardView[] = [];
  private cardStreams = new Map<string, MediaStream>();

  // Hand menu.
  private readonly menuEntity: Entity;
  private readonly menuUrl: string;
  private menuDoc: UIKitDocument | null = null;
  private tiles: HandMenuTileState[];
  private readonly anchor = new HandMenuAnchor();
  private latestPalm: PalmPose | null = null;
  private latestGrip: GripPose | null = null;
  private readonly stopXRFrame: () => void;
  private pendingReadout: { eyebrow: string; title: string } = { eyebrow: 'STEP', title: '—' };

  // Location indicator.
  private hintGroup: Group | null = null;
  private hintMarker: Mesh | null = null;
  private hintLine: Line | null = null;
  private hintLineGeometry: BufferGeometry | null = null;
  private pillEntity: Entity | null = null;
  private pillUrl: string | null = null;
  private pillDoc: UIKitDocument | null = null;
  private pendingLabel: string | null = null;
  private hintVisible = false;

  private disposed = false;

  constructor(options: TrainingUxHostOptions) {
    this.world = options.world;
    this.palette = options.palette;
    this.settings = { ...DEFAULT_UX_SETTINGS, ...options.settings };
    this.onOptionPressed = options.onOptionPressed;
    this.brand = options.brand ?? 'TRAINING';
    this.offHand = offHandOf(options.pointerHand ?? 'right');
    this.tiles = buildHandMenuTiles(options.menuActions);

    const formDistance = options.formDistanceMeters ?? this.settings.windowDistanceMeters;

    this.formUrl = markupUrl(STEP_FORM_MARKUP);
    this.formEntity = this.world.createTransformEntity().addComponent(PanelUI, { config: this.formUrl });
    this.formEntity.object3D!.visible = false;
    this.formFollower = new WindowFollower(this.formEntity.object3D!, {
      mode: this.settings.windowPlacement,
      distanceMeters: formDistance,
      heightOffsetMeters: -0.04,
      followSeconds: this.settings.windowFollowSeconds,
      clearanceDegrees: this.settings.labelClearanceDegrees,
    });

    this.menuUrl = markupUrl(HAND_MENU_MARKUP);
    this.menuEntity = this.world.createTransformEntity().addComponent(PanelUI, { config: this.menuUrl });
    this.menuEntity.object3D!.visible = false;

    // Hand poses are only readable inside an XR frame; cache the latest and
    // let update() consume them.
    this.stopXRFrame = this.world.onXRFrame((frame) => this.readHandPoses(frame));
  }

  /** Is the step form's content on screen? (The assistant grid can be up without it.) */
  get isFormVisible(): boolean {
    return this.formContentVisible;
  }

  private presented = true;

  /**
   * Master visibility - the host app hides the whole UX while no XR session
   * is running (the flat warm-up page owns the screen then) and shows it on
   * session start. Content state is untouched; it comes back as it was.
   */
  setVisible(visible: boolean): void {
    if (this.presented === visible) return;
    this.presented = visible;
    this.applyPresented();
  }

  private applyPresented(): void {
    const form = this.formEntity.object3D;
    if (form) form.visible = this.presented && (this.formContentVisible || this.assistantCards.length > 0);
    const menu = this.menuEntity.object3D;
    if (menu && !this.presented) menu.visible = false;
    if (this.hintGroup) this.hintGroup.visible = this.presented && this.hintVisible;
    if (this.pillEntity?.object3D) this.pillEntity.object3D.visible = this.presented && this.hintVisible;
  }

  // ------------------------------------------------------------- step form

  /** Show (or hide, for pass-through steps) the form for a step. */
  showStep(step: TrainingStepView): void {
    if (!hasPresentation(step)) {
      this.setFormContentVisible(false);
      return;
    }
    this.pendingStep = step;
    if (this.formDoc) this.renderStep(step);
    this.setFormContentVisible(true);
    this.formFollower?.reanchor(this.viewerPose());
  }

  /** Re-anchor the current form in front of the user (hand menu TASKS). */
  representForm(): void {
    if (this.formContentVisible) this.formFollower?.reanchor(this.viewerPose());
  }

  hideForm(): void {
    this.setFormContentVisible(false);
  }

  /** Enable/disable the form's action buttons. Sticky across later steps until re-enabled. */
  setOptionsInteractable(interactable: boolean): void {
    this.optionsInteractable = interactable;
    if (this.formDoc && this.pendingStep) this.renderOptions(this.pendingStep);
  }

  private setFormContentVisible(visible: boolean): void {
    this.formContentVisible = visible;
    const object = this.formEntity.object3D;
    if (object) object.visible = this.presented && (visible || this.assistantCards.length > 0);
    this.el(this.formDoc, STEP_FORM_IDS.panel)?.setProperties({ display: visible ? 'flex' : 'none' });
  }

  private renderStep(step: TrainingStepView): void {
    const T = this.palette;
    const doc = this.formDoc;
    if (!doc) return;

    this.el(doc, STEP_FORM_IDS.panel)?.setProperties({
      backgroundColor: panel(T),
      borderColor: panelBorder(T),
    });

    // A view with no step count is not a step (picker/help): it keeps the
    // brand eyebrow but drops the counter and the tick row.
    const isStep = step.stepCount > 0;
    this.el(doc, STEP_FORM_IDS.counter)?.setProperties({
      text: isStep ? `STEP ${step.stepIndex + 1} OF ${step.stepCount}` : '',
      color: T.accent,
      display: isStep ? 'flex' : 'none',
    });
    this.el(doc, STEP_FORM_IDS.brand)?.setProperties({ text: this.brand, color: T.textLo });

    this.el(doc, STEP_FORM_IDS.ticks)?.setProperties({ display: isStep ? 'flex' : 'none' });
    for (let i = 0; i < MAX_TICKS; i += 1) {
      const present = isStep && i < step.stepCount;
      const color = i < step.stepIndex ? T.accent : i === step.stepIndex ? accentA(T, 0.5) : overlay(T, 0.12);
      this.el(doc, STEP_FORM_IDS.tick(i))?.setProperties({
        display: present ? 'flex' : 'none',
        backgroundColor: color,
      });
    }

    this.el(doc, STEP_FORM_IDS.title)?.setProperties({ text: step.title, color: T.textHi });
    this.el(doc, STEP_FORM_IDS.description)?.setProperties({
      text: step.description,
      color: T.textMid,
      display: step.description.length > 0 ? 'flex' : 'none',
    });

    // Step image - rendered only when the host resolved one (no image, no block).
    this.el(doc, STEP_FORM_IDS.imagePlate)?.setProperties({
      display: step.imageUrl ? 'flex' : 'none',
      borderColor: overlay(T, 0.18),
    });
    if (step.imageUrl) this.el(doc, STEP_FORM_IDS.image)?.setProperties({ src: step.imageUrl });

    this.renderOptions(step);

    this.el(doc, STEP_FORM_IDS.hint)?.setProperties({
      text: 'Point, then pinch or pull the trigger to select · Turn palm up for menu',
      color: T.textLo,
    });
  }

  private renderOptions(step: TrainingStepView): void {
    const T = this.palette;
    const doc = this.formDoc;
    if (!doc) return;
    this.optionCount = Math.min(step.options.length, MAX_OPTIONS);
    this.el(doc, STEP_FORM_IDS.actions)?.setProperties({ display: this.optionCount > 0 ? 'flex' : 'none' });
    for (let i = 0; i < MAX_OPTIONS; i += 1) {
      const present = i < this.optionCount;
      const primary = i === 0;
      const enabled = this.optionsInteractable;
      this.el(doc, STEP_FORM_IDS.option(i))?.setProperties({
        display: present ? 'flex' : 'none',
        backgroundColor: primary ? T.accent : secondaryBg(T),
        borderColor: primary ? T.accent : secondaryBorder(T),
        opacity: enabled ? 1 : 0.5,
      });
      this.el(doc, STEP_FORM_IDS.optionLabel(i))?.setProperties({
        text: present ? step.options[i] : '',
        color: primary ? T.onAccent : T.secondaryText,
      });
    }
  }

  // -------------------------------------------------------- assistant grid

  /**
   * Replace the grid of remote-assistance cards above the step form - one card
   * per connected assistant. An empty list takes the grid away entirely.
   */
  setAssistants(assistants: readonly AssistantCardView[]): void {
    this.assistantCards = assistants.slice(0, MAX_ASSISTANT_CARDS);
    this.renderCards();
    this.setFormContentVisible(this.formContentVisible);
  }

  private renderCards(): void {
    const doc = this.formDoc;
    if (!doc) return;
    const T = this.palette;
    this.el(doc, STEP_FORM_IDS.grid)?.setProperties({ display: this.assistantCards.length > 0 ? 'flex' : 'none' });

    const keep = new Set<string>();
    for (let i = 0; i < MAX_ASSISTANT_CARDS; i += 1) {
      const card = this.assistantCards[i];
      const present = card !== undefined;
      this.el(doc, STEP_FORM_IDS.card(i))?.setProperties({
        display: present ? 'flex' : 'none',
        borderColor: overlay(T, 0.18),
      });
      if (!card) continue;
      keep.add(card.id);

      const hasVideo = card.video !== null;
      let stream = this.cardStreams.get(card.id);
      if (hasVideo && (!stream || stream.getVideoTracks()[0] !== card.video)) {
        stream = new MediaStream([card.video!]);
        this.cardStreams.set(card.id, stream);
      }
      this.el(doc, STEP_FORM_IDS.cardVideo(i))?.setProperties({
        display: hasVideo ? 'flex' : 'none',
        ...(hasVideo && stream ? { src: stream } : {}),
      });
      this.el(doc, STEP_FORM_IDS.cardName(i))?.setProperties({ text: card.name, display: hasVideo ? 'none' : 'flex' });
      this.el(doc, STEP_FORM_IDS.cardCorner(i))?.setProperties({ text: card.name, display: hasVideo ? 'flex' : 'none' });
      this.el(doc, STEP_FORM_IDS.cardMuted(i))?.setProperties({
        display: card.muted ? 'flex' : 'none',
        backgroundColor: T.danger,
      });
    }
    for (const id of [...this.cardStreams.keys()]) if (!keep.has(id)) this.cardStreams.delete(id);
  }

  // ------------------------------------------------------------- hand menu

  setHandMenuStep(eyebrow: string, title: string): void {
    this.pendingReadout = { eyebrow, title };
    this.el(this.menuDoc, HAND_MENU_IDS.stepEyebrow)?.setProperties({ text: eyebrow });
    this.el(this.menuDoc, HAND_MENU_IDS.stepTitle)?.setProperties({ text: title });
  }

  /** Push the wearer's microphone state onto its tile. No-op when the host never offered the toggle. */
  setHandMenuMic(glyph: string, label: string, available: boolean, on: boolean): void {
    this.setAudioTile('mic', glyph, label, available, on);
  }

  /** Push the assistant-audio state onto its tile. */
  setHandMenuAssistantAudio(glyph: string, label: string, available: boolean, on: boolean): void {
    this.setAudioTile('hear', glyph, label, available, on);
  }

  private setAudioTile(id: 'mic' | 'hear', glyph: string, label: string, available: boolean, on: boolean): void {
    this.tiles = this.tiles.map((tile) =>
      tile.id === id && tile.present ? withAudioState(tile, glyph, label, available, on) : tile,
    );
    this.renderTiles();
  }

  private renderTiles(): void {
    const doc = this.menuDoc;
    if (!doc) return;
    const T = this.palette;
    this.el(doc, HAND_MENU_IDS.root)?.setProperties({ backgroundColor: panel(T), borderColor: panelBorder(T) });
    this.el(doc, 'hm-divider-top')?.setProperties({ backgroundColor: overlay(T, 0.12) });
    this.el(doc, 'hm-divider-bottom')?.setProperties({ backgroundColor: overlay(T, 0.12) });
    this.el(doc, HAND_MENU_IDS.stepEyebrow)?.setProperties({ color: T.accent, text: this.pendingReadout.eyebrow });
    this.el(doc, HAND_MENU_IDS.stepTitle)?.setProperties({ color: T.textMid, text: this.pendingReadout.title });

    for (const tile of this.tiles) {
      const colors = tileColors(T, tile.enabled, tile.active, tile.danger);
      this.el(doc, HAND_MENU_IDS.tile(tile.id))?.setProperties({
        display: tile.present ? 'flex' : 'none',
        backgroundColor: colors.fill,
        borderColor: colors.border,
        hover: { backgroundColor: colors.fillHover },
        opacity: tile.enabled ? 1 : 0.5,
      });
      // The Unity strip's glyphs (⌂ ☰ ⟳ ☎ ○ ● ◇ ◈ ⌫ ✕) are outside the MSDF
      // font atlases IWSDK ships, and render as boxes. The label already
      // names the state, so glyphs the atlas cannot draw are hidden rather
      // than shown broken; ASCII-safe ones still render.
      const glyphSafe = /^[\x20-\x7e]+$/.test(tile.glyph);
      this.el(doc, HAND_MENU_IDS.tileGlyph(tile.id))?.setProperties({
        text: tile.glyph,
        color: colors.icon,
        display: glyphSafe ? 'flex' : 'none',
      });
      this.el(doc, HAND_MENU_IDS.tileLabel(tile.id))?.setProperties({ text: tile.label, color: colors.icon });
    }
  }

  private wireTiles(doc: UIKitDocument): void {
    for (const tile of this.tiles) {
      this.el(doc, HAND_MENU_IDS.tile(tile.id))?.addEventListener('click', () => {
        // Same contract as a disabled DOM button: a press does nothing unless enabled.
        const current = this.tiles.find((t) => t.id === tile.id);
        if (!current?.present || !current.enabled || !this.anchor.current.shown) return;
        current.onPress?.();
      });
    }
  }

  // ---------------------------------------------------- location indicator

  /** Place (or move) the hotspot marker + connector + label pill at a world point. */
  showWorldLabel(worldPoint: Vec3, labelText: string): void {
    if (!this.hintGroup) this.buildWorldLabel();
    this.hintVisible = true;
    this.hintGroup!.visible = this.presented;
    this.pendingLabel = labelText;
    this.el(this.pillDoc, LABEL_PILL_IDS.text)?.setProperties({ text: labelText });
    if (this.pillEntity?.object3D) this.pillEntity.object3D.visible = this.presented;
    this.updateWorldLabel(worldPoint);
  }

  /** Refresh the indicator position (e.g. on a re-sighting of the tracked object). */
  updateWorldLabel(worldPoint: Vec3): void {
    if (!this.hintVisible || !this.hintMarker || !this.hintLineGeometry || !this.pillEntity?.object3D) return;
    this.hintMarker.position.set(worldPoint.x, worldPoint.y, worldPoint.z);
    const pill = this.pillEntity.object3D;
    pill.position.set(worldPoint.x, worldPoint.y + LABEL_LIFT_METERS, worldPoint.z);
    // Attach the connector to the pill's bottom edge (half its scaled height).
    const pillHalfHeight = 56 * 0.5 * this.settings.labelScale;
    this.hintLineGeometry.setFromPoints([
      new Vector3(worldPoint.x, worldPoint.y, worldPoint.z),
      new Vector3(pill.position.x, pill.position.y - pillHalfHeight, pill.position.z),
    ]);
  }

  hideWorldLabel(): void {
    this.hintVisible = false;
    if (this.hintGroup) this.hintGroup.visible = false;
    if (this.pillEntity?.object3D) this.pillEntity.object3D.visible = false;
  }

  private buildWorldLabel(): void {
    const T = this.palette;
    this.hintGroup = new Group();
    this.hintGroup.name = 'UXTraining_Hint';
    this.world.scene.add(this.hintGroup);

    // Marker dot at the indicated world point.
    this.hintMarker = new Mesh(
      new SphereGeometry(this.settings.labelDotDiameterMeters / 2, 16, 12),
      new MeshBasicMaterial({ color: T.accent }),
    );
    this.hintGroup.add(this.hintMarker);

    // Leader-line connector up to the pill.
    this.hintLineGeometry = new BufferGeometry().setFromPoints([new Vector3(), new Vector3()]);
    this.hintLine = new Line(
      this.hintLineGeometry,
      new LineBasicMaterial({ color: T.accent, transparent: true, opacity: 0.85 }),
    );
    this.hintGroup.add(this.hintLine);

    // Label pill - a billboarded panel.
    this.pillUrl = markupUrl(LABEL_PILL_MARKUP);
    this.pillEntity = this.world.createTransformEntity().addComponent(PanelUI, { config: this.pillUrl });
    this.pillEntity.object3D!.visible = false;

    // Head-locked windows must keep clear of the label while it shows.
    WindowFollower.registerObstacle(this.pillEntity.object3D!, () =>
      this.hintVisible && this.pillEntity?.object3D
        ? v3(this.pillEntity.object3D.position.x, this.pillEntity.object3D.position.y, this.pillEntity.object3D.position.z)
        : null,
    );
  }

  // ------------------------------------------------------------------ frame

  /** Per-frame drive: panel adoption, follower, hand anchor, billboarding. Call from an ECS system. */
  update(deltaSeconds: number): void {
    if (this.disposed) return;
    this.adoptDocuments();

    const viewer = this.viewerPose();
    this.formFollower?.update(viewer, deltaSeconds);

    // Hand menu placement + palm-up fade.
    const menu = this.menuEntity.object3D;
    if (menu && this.menuDoc && this.presented) {
      const out = this.anchor.update(
        { viewer, palm: this.latestPalm, grip: this.latestGrip, offHand: this.offHand },
        deltaSeconds,
      );
      menu.position.set(out.position.x, out.position.y, out.position.z);
      menu.lookAt(viewer.position.x, viewer.position.y, viewer.position.z);
      menu.visible = out.alpha > 0.01;
      this.el(this.menuDoc, HAND_MENU_IDS.root)?.setProperties({
        opacity: out.alpha,
        pointerEvents: out.shown ? 'auto' : 'none',
      });
    }

    // Billboard the pill toward the viewer.
    if (this.hintVisible && this.presented && this.pillEntity?.object3D) {
      this.pillEntity.object3D.lookAt(viewer.position.x, viewer.position.y, viewer.position.z);
    }
  }

  /** Panels load asynchronously; bind each document the first time it appears. */
  private adoptDocuments(): void {
    if (!this.formDoc) {
      const doc = this.documentOf(this.formEntity);
      if (doc) {
        this.formDoc = doc;
        doc.setTargetDimensions(FORM_WIDTH_PX * FORM_PIXELS_TO_METRES, 10);
        for (let i = 0; i < MAX_OPTIONS; i += 1) {
          this.el(doc, STEP_FORM_IDS.option(i))?.addEventListener('click', () => {
            if (this.optionsInteractable && i < this.optionCount) this.onOptionPressed(i);
          });
        }
        if (this.pendingStep) this.renderStep(this.pendingStep);
        this.renderCards();
        this.setFormContentVisible(this.formContentVisible);
      }
    }

    if (!this.menuDoc) {
      const doc = this.documentOf(this.menuEntity);
      if (doc) {
        this.menuDoc = doc;
        doc.setTargetDimensions(MENU_WIDTH_PX * MENU_PIXELS_TO_METRES, 10);
        this.wireTiles(doc);
        this.renderTiles();
        this.el(doc, HAND_MENU_IDS.root)?.setProperties({ opacity: 0, pointerEvents: 'none' });
      }
    }

    if (this.pillEntity && !this.pillDoc) {
      const doc = this.documentOf(this.pillEntity);
      if (doc) {
        this.pillDoc = doc;
        doc.setTargetDimensions(280 * this.settings.labelScale, 56 * this.settings.labelScale);
        this.el(doc, LABEL_PILL_IDS.root)?.setProperties({ backgroundColor: panel(this.palette), borderColor: hintBorder(this.palette) });
        this.el(doc, LABEL_PILL_IDS.text)?.setProperties({ text: this.pendingLabel ?? '', color: this.palette.textHi });
      }
    }
  }

  private documentOf(entity: Entity): UIKitDocument | null {
    const doc = (PanelDocument as unknown as { data: { document: unknown[] } }).data.document[entity.index];
    return (doc as UIKitDocument | undefined) ?? null;
  }

  private el(doc: UIKitDocument | null, id: string): UiElement | null {
    if (!doc) return null;
    return (doc.getElementById(id) as unknown as UiElement | null) ?? null;
  }

  private readHandPoses(frame: XRFrame): void {
    const session = this.world.session;
    const referenceSpace = this.world.renderer.xr.getReferenceSpace();
    if (!session || !referenceSpace) {
      this.latestPalm = null;
      this.latestGrip = null;
      return;
    }
    const player = this.world.player;
    const origin = {
      position: v3(player.position.x, player.position.y, player.position.z),
      quaternion: { x: player.quaternion.x, y: player.quaternion.y, z: player.quaternion.z, w: player.quaternion.w },
    };
    this.latestPalm = readPalmPose(frame, session, referenceSpace, this.offHand, origin);
    this.latestGrip = this.latestPalm ? null : readGripPose(frame, session, referenceSpace, this.offHand, origin);
  }

  private viewerPose(): ViewerPose {
    const camera = this.world.camera;
    const position = new Vector3();
    camera.getWorldPosition(position);
    const forward = new Vector3();
    camera.getWorldDirection(forward);
    const right = new Vector3(1, 0, 0).applyQuaternion(camera.getWorldQuaternion(new Quaternion()));
    return {
      position: v3(position.x, position.y, position.z),
      forward: v3(forward.x, forward.y, forward.z),
      right: v3(right.x, right.y, right.z),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopXRFrame();
    if (this.pillEntity?.object3D) WindowFollower.unregisterObstacle(this.pillEntity.object3D);
    this.hintGroup?.removeFromParent();
    this.hintLineGeometry?.dispose();
    (this.hintMarker?.geometry as BufferGeometry | undefined)?.dispose();
    for (const url of [this.formUrl, this.menuUrl, this.pillUrl]) if (url) URL.revokeObjectURL(url);
    this.formEntity.destroy();
    this.menuEntity.destroy();
    this.pillEntity?.destroy();
  }
}

/** Convenience factory (mirrors the Unity `TrainingUxController.Initialize`). */
export function createTrainingUxHost(options: TrainingUxHostOptions): TrainingUxHost {
  return new TrainingUxHost(options);
}

/** Colours a host may want for secondary surfaces built outside the kit. */
export const formColors = { dangerBg, dangerBorder, dangerText };
